/**
 * 예산관리 기본정보(BUD_BASIC_01) 컬럼 정의
 * BUD_BASIC_01 화면 전용 컬럼 정의
 */

import { CreateColumn } from '../../../../Services/agGrid/createColumn';
import CustomHeader from '../../../../Components/AgGrid/CustomHeader';
import { GridSuggestionTextCellEditor } from '../../../../Components/AgGrid/GridSuggestionTextCellEditor';
import {
  createBudgetAmountFormatter,
  createBudgetAmountParser,
  DEFAULT_AMOUNT_UNIT,
} from '../budgetAmountUnit';

/**
 * 예산관리 기본정보 컬럼 정의 생성
 * @param {object} props - 컴포넌트 props
 * @returns {Array} 컬럼 정의 배열
 */
export const getColumnDefs1 = (props) => {
  const isQuarterView = props.viewMode === 'quarter';
  const amountUnit = props.amountUnit ?? DEFAULT_AMOUNT_UNIT;

  const numberFormatter = createBudgetAmountFormatter(amountUnit);
  const numberParser = createBudgetAmountParser(amountUnit);

  // 월별 컬럼 스타일 (합계 행 처리)
  const monthCellStyle = (params) => {
    const baseStyle = { textAlign: 'right' };
    if (params.data?.isSummaryRow) {
      return {
        ...baseStyle,
        backgroundColor: '#e8f5e9',
        cursor: 'not-allowed',
        fontWeight: 'bold',
      };
    }
    return baseStyle;
  };

  // 월별 컬럼 편집 가능 여부
  const monthEditable = (params) =>
    params.node?.rowPinned !== 'bottom' && !params.data?.isSummaryRow;

  const categoryItemEditable = (params) =>
    !params.data?.isSummaryRow && params.node?.rowPinned !== 'bottom';

  const getUniqueValuesFromGrid = (field, currentValue = '') => {
    const values = new Set();
    const gridApi = props.gridRef1?.current?.api;
    if (gridApi) {
      gridApi.forEachNode((node) => {
        const row = node?.data;
        if (!row || row?.isSummaryRow) return;
        const value = String(row?.[field] ?? '').trim();
        if (!value) return;
        values.add(value);
      });
    } else {
      (props.rowData1 || []).forEach((row) => {
        if (!row || row?.isSummaryRow) return;
        const value = String(row?.[field] ?? '').trim();
        if (!value) return;
        values.add(value);
      });
    }
    const current = String(currentValue ?? '').trim();
    if (current) values.add(current);
    return Array.from(values);
  };

  const categoryItemCellStyle = (params) => {
    if (params.node?.rowPinned === 'bottom') {
      return {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'not-allowed',
        backgroundColor: '#e3f2fd',
        color: '#000',
        fontWeight: 'bold',
        textAlign: 'center',
      };
    }
    if (params.data?.isSummaryRow) {
      return {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'not-allowed',
        backgroundColor: '#e8f5e9',
        color: '#000',
        fontWeight: 'bold',
        textAlign: 'center',
      };
    }
    return {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      cursor: 'text',
      backgroundColor: '#ffffff',
      color: '#1f2328',
    };
  };

  return [
    CreateColumn('no', 'NO', 'center', 80, false, false, false, 'checkbox', {
      maxWidth: 80,
      cellClass: 'delete-action-cell',
      checkboxProps: {
        headerCheckboxSelection: false,
        DeleteNewRow: props.DeleteNewRow1,
        RestoreRow: props.RestoreRow1,
        gridRef: props.gridRef1,
        rowData: props.rowData1,
        selectedRowNosRef: props.selectedRowNosRef1,
        lastCheckedIndexRef: props.lastCheckedIndexRef1,
        handleRowSelection: props.handleRowSelection1,
        // 정렬/필터 후에도 node.data 기준으로 안전하게 선택 처리
        preferNodeRowData: true,
        hideCheckboxCondition: (params) =>
          params?.data?.isSummaryRow ||
          params?.node?.rowPinned === 'bottom' ||
          params?.data?.no === '합계',
      },
    }),

    // 구분 컬럼
    CreateColumn(
      'categoryName',
      '구분',
      'left',
      200,
      true,
      true,
      false,
      'text',
      {
        minWidth: 200,
        editableCondition: categoryItemEditable,
        maxLength: 100,
        cellEditor: GridSuggestionTextCellEditor,
        cellEditorParams: (params) => ({
          maxLength: 100,
          getSuggestions: () =>
            getUniqueValuesFromGrid('categoryName', params?.value),
        }),
        customCellStyle: categoryItemCellStyle,
        cellRenderer: (params) => {
          const currentValue = params.value;

          if (!currentValue) return '';

          // 최하단 합계 행인 경우 "합계" 표시
          if (params.node?.rowPinned === 'bottom') {
            return '합계';
          }

          // 구분별 합계 행인 경우 "합계" 표시
          if (params.data?.isSummaryRow) {
            return '합계';
          }

          // 현재 행의 인덱스
          const currentIndex = params.node.rowIndex;

          // 이전 행의 값 확인
          if (currentIndex > 0) {
            const prevRow = params.api.getDisplayedRowAtIndex(currentIndex - 1);
            if (
              prevRow &&
              prevRow.data &&
              !prevRow.data.isSummaryRow &&
              prevRow.data.categoryName === currentValue
            ) {
              // 이전 행과 값이 같으면 빈 문자열 반환 (셀 병합 효과)
              return '';
            }
          }

          // 첫 번째 행이거나 이전 행과 값이 다를 때만 값 표시
          return currentValue;
        },
        colSpan: (params) => {
          // 최종 합계 행 또는 구분별 합계 행인 경우 구분상세까지 병합 (2개 컬럼)
          if (
            params.data?.isSummaryRow ||
            params.node?.rowPinned === 'bottom'
          ) {
            return 2; // categoryName + itemName 병합
          }
          return 1;
        },
      }
    ),
    // 구분상세 컬럼
    CreateColumn(
      'itemName',
      '구분상세',
      'left',
      200,
      true,
      true,
      false,
      'text',
      {
        minWidth: 200,
        editableCondition: categoryItemEditable,
        maxLength: 150,
        cellEditor: GridSuggestionTextCellEditor,
        cellEditorParams: (params) => ({
          maxLength: 150,
          getSuggestions: () =>
            getUniqueValuesFromGrid('itemName', params?.value),
        }),
        customCellStyle: (params) => {
          if (params.data?.isSummaryRow) {
            return {
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              cursor: 'not-allowed',
              backgroundColor: '#e8f5e9',
              color: '#000',
              fontWeight: 'bold',
            };
          }
          if (params.node?.rowPinned === 'bottom') {
            return categoryItemCellStyle(params);
          }
          return {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            cursor: 'text',
            backgroundColor: '#ffffff',
            color: '#1f2328',
          };
        },
        colSpan: (params) => {
          // 합계 행인 경우 숨김 (구분 컬럼에서 병합됨)
          if (params.data?.isSummaryRow) {
            return 0;
          }
          return params.node?.rowPinned === 'bottom' ? 0 : 1;
        },
      }
    ),
    // 연간예산 총액 컬럼
    CreateColumn(
      'currentYearPlanAmount',
      '연간예산 총액',
      'right',
      150,
      false,
      false,
      false,
      'text',
      {
        minWidth: 150,
        headerComponentParams: {
          template:
            '<div class="ag-header-cell-label"><span>연간<br>예산 총액</span></div>',
        },
        customCellStyle: (params) => {
          const baseStyle = {
            textAlign: 'right',
            fontWeight: 'bold',
            cursor: 'not-allowed',
          };
          if (params.node?.rowPinned === 'bottom') {
            return {
              ...baseStyle,
              backgroundColor: '#e3f2fd', // 최종 합계 행 색상
            };
          }
          if (params.data?.isSummaryRow) {
            return {
              ...baseStyle,
              backgroundColor: '#e8f5e9', // 구분별 합계 행 색상
            };
          }
          return {
            ...baseStyle,
            backgroundColor: '#f5f5f5',
          };
        },
        valueFormatter: numberFormatter,
      }
    ),
    // 월별 결산 집행 내역 그룹
    {
      headerName: '월별 결산 집행 내역',
      headerClass: 'header-align-center',
      children: [
        // 1월
        CreateColumn(
          'month01',
          '1월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 2월
        CreateColumn(
          'month02',
          '2월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 3월
        CreateColumn(
          'month03',
          '3월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 합계(1분기)
        CreateColumn(
          'q1Total',
          '합계(1분기)',
          'right',
          120,
          false,
          false,
          !isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(1분기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month1 = parseFloat(row.month01) || 0;
              const month2 = parseFloat(row.month02) || 0;
              const month3 = parseFloat(row.month03) || 0;
              return month1 + month2 + month3;
            },
          }
        ),
        // 4월
        CreateColumn(
          'month04',
          '4월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 5월
        CreateColumn(
          'month05',
          '5월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 6월
        CreateColumn(
          'month06',
          '6월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 합계(2분기)
        CreateColumn(
          'q2Total',
          '합계(2분기)',
          'right',
          120,
          false,
          false,
          !isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(2분기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month4 = parseFloat(row.month04) || 0;
              const month5 = parseFloat(row.month05) || 0;
              const month6 = parseFloat(row.month06) || 0;
              return month4 + month5 + month6;
            },
          }
        ),
        // 합계(상반기)
        CreateColumn(
          'h1Total',
          '합계(상반기)',
          'right',
          120,
          false,
          false,
          isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(상반기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month1 = parseFloat(row.month01) || 0;
              const month2 = parseFloat(row.month02) || 0;
              const month3 = parseFloat(row.month03) || 0;
              const month4 = parseFloat(row.month04) || 0;
              const month5 = parseFloat(row.month05) || 0;
              const month6 = parseFloat(row.month06) || 0;
              return month1 + month2 + month3 + month4 + month5 + month6;
            },
          }
        ),
        // 7월
        CreateColumn(
          'month07',
          '7월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 8월
        CreateColumn(
          'month08',
          '8월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 9월
        CreateColumn(
          'month09',
          '9월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 합계(3분기)
        CreateColumn(
          'q3Total',
          '합계(3분기)',
          'right',
          120,
          false,
          false,
          !isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(3분기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month7 = parseFloat(row.month07) || 0;
              const month8 = parseFloat(row.month08) || 0;
              const month9 = parseFloat(row.month09) || 0;
              return month7 + month8 + month9;
            },
          }
        ),
        // 10월
        CreateColumn(
          'month10',
          '10월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 11월
        CreateColumn(
          'month11',
          '11월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 12월
        CreateColumn(
          'month12',
          '12월',
          'right',
          120,
          true,
          false,
          false,
          'text',
          {
            minWidth: 120,
            editableCondition: monthEditable,
            customCellStyle: monthCellStyle,
            valueFormatter: numberFormatter,
            valueParser: numberParser,
          }
        ),
        // 합계(4분기)
        CreateColumn(
          'q4Total',
          '합계(4분기)',
          'right',
          120,
          false,
          false,
          !isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(4분기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month10 = parseFloat(row.month10) || 0;
              const month11 = parseFloat(row.month11) || 0;
              const month12 = parseFloat(row.month12) || 0;
              return month10 + month11 + month12;
            },
          }
        ),
        // 합계(하반기)
        CreateColumn(
          'h2Total',
          '합계(하반기)',
          'right',
          120,
          false,
          false,
          isQuarterView,
          'text',
          {
            minWidth: 120,
            headerComponent: CustomHeader,
            headerComponentParams: {
              template:
                '<div class="ag-header-cell-label"><span>합계<br>(하반기)</span></div>',
            },
            customCellStyle: (params) => {
              const baseStyle = {
                textAlign: 'right',
                backgroundColor: '#e8f5e9',
              };
              if (params.data?.isSummaryRow) {
                return {
                  ...baseStyle,
                  backgroundColor: '#e8f5e9',
                  cursor: 'not-allowed',
                  fontWeight: 'bold',
                };
              }
              return baseStyle;
            },
            valueFormatter: numberFormatter,
            valueGetter: (params) => {
              const row = params.data || {};
              const month7 = parseFloat(row.month07) || 0;
              const month8 = parseFloat(row.month08) || 0;
              const month9 = parseFloat(row.month09) || 0;
              const month10 = parseFloat(row.month10) || 0;
              const month11 = parseFloat(row.month11) || 0;
              const month12 = parseFloat(row.month12) || 0;
              return month7 + month8 + month9 + month10 + month11 + month12;
            },
          }
        ),
      ],
    },
    // 합계 컬럼
    CreateColumn(
      'totalYearAmount',
      '합계',
      'right',
      150,
      false,
      false,
      false,
      'text',
      {
        minWidth: 150,
        customCellStyle: (params) => {
          const baseStyle = {
            textAlign: 'right',
            fontWeight: 'bold',
            cursor: 'not-allowed',
          };
          if (params.node?.rowPinned === 'bottom') {
            return {
              ...baseStyle,
              backgroundColor: '#e3f2fd', // 최종 합계 행 색상
            };
          }
          if (params.data?.isSummaryRow) {
            return {
              ...baseStyle,
              backgroundColor: '#e8f5e9', // 구분별 합계 행 색상
            };
          }
          return {
            ...baseStyle,
            backgroundColor: '#f5f5f5',
          };
        },
        valueFormatter: numberFormatter,
      }
    ),
    // 예산 대비 집행 실적 컬럼
    CreateColumn(
      'execVsPlanRate',
      '예산 대비 집행 실적',
      'right',
      150,
      false,
      false,
      false,
      'text',
      {
        width: 150,
        headerComponent: CustomHeader,
        headerComponentParams: {
          template:
            '<div class="ag-header-cell-label"><span>예산 대비<br>집행 실적</span></div>',
        },
        headerClass: 'header-align-center',
        customCellStyle: (params) => {
          const baseStyle = {
            textAlign: 'right',
            cursor: 'not-allowed',
          };
          if (params.node?.rowPinned === 'bottom') {
            return {
              ...baseStyle,
              backgroundColor: '#e3f2fd', // 최종 합계 행 색상
              fontWeight: 'bold',
            };
          }
          if (params.data?.isSummaryRow) {
            return {
              ...baseStyle,
              backgroundColor: '#e8f5e9', // 구분별 합계 행 색상
              fontWeight: 'bold',
            };
          }
          return {
            ...baseStyle,
            backgroundColor: '#f5f5f5',
          };
        },
        valueFormatter: (params) => {
          if (!params.value || params.value === 0) return '0%';
          return `${params.value}%`;
        },
      }
    ),
    // 비고 컬럼
    CreateColumn('remark', '비고', 'left', 200, true, false, false, 'text', {
      minWidth: 200,
      flex: 1,
      editableCondition: (params) =>
        params.node?.rowPinned !== 'bottom' && !params.data?.isSummaryRow,
      customCellStyle: (params) => {
        const baseStyle = {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        };
        if (params.node?.rowPinned === 'bottom') {
          return {
            ...baseStyle,
            backgroundColor: '#e3f2fd', // 최종 합계 행 색상
            cursor: 'not-allowed',
            fontWeight: 'bold',
          };
        }
        if (params.data?.isSummaryRow) {
          return {
            ...baseStyle,
            backgroundColor: '#e8f5e9', // 구분별 합계 행 색상
            cursor: 'not-allowed',
            fontWeight: 'bold',
          };
        }
        return baseStyle;
      },
      maxLength: 200,
    }),
  ];
};

const columnDefs = {
  getColumnDefs1,
};

export default columnDefs;
